import { createSelector } from '@reduxjs/toolkit';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import {
    LOAD_CATALOG_ITEM_MODELS_FAIL,
    LOAD_CATALOG_ITEM_MODELS_REQUEST,
    LOAD_CATALOG_ITEM_MODELS_SUCCESS,
    LOAD_CATALOG_ITEM_MODELS_SUCCESS_ACTION,
} from './actions';
import cloneDeep from 'lodash/cloneDeep';
import itemCardModelApi, { FetchItemsCardModelsByCatalogIdQueryType } from '@/redux/api/itemCardModel';

export const DEFAULT_STATE = {
    byId: {},
    loaded: 0,
    loading: false,
    pagination: {
        keyword: '',
        page: 1,
        pageSize: 24,
        results: 0,
        sort: 'lotNumber',
        totalRecords: 0,
    },
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_CATALOG_ITEM_MODELS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_CATALOG_ITEM_MODELS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_CATALOG_ITEM_MODELS_SUCCESS:
            // we dont want to save our cursored paging data in here
            if (typeof action.meta.query.index === 'undefined') {
                const ids = action.payload.map((i) => i.itemId);
                const existing: State['byId'] = cloneDeep(state.byId);
                existing[action.meta.catalogId] = ids;

                return {
                    ...state,
                    byId: existing,
                    loaded: action.meta.actionTime,
                    loading: false,
                    pagination: {
                        ...DEFAULT_STATE.pagination,
                        ...action.meta.query,
                        totalRecords: action.meta.recordCount || action.payload.length || 0,
                    },
                };
            }
            return {
                ...state,
                loading: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.catalogItems;
const idSelector = (state: GlobalState, id: number) => id;
const byIdSelector = createSelector(stateSelector, (state) => state.byId);

export const getCatalogItemIdsByCatalogId: (state: any, selector: any) => number[] = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => {
        return byId[id] || [];
    }
);

export const getCatalogItemsLoading = createSelector(stateSelector, (state) => state.loading);

export const getCatalogItemsPagination = createSelector(stateSelector, (state) => ({
    ...DEFAULT_STATE.pagination,
    ...state.pagination,
}));

/* ACTION CREATORS */
export const fetchCatalogItemModels =
    ({ catalogId, query }: { catalogId: number; query: FetchItemsCardModelsByCatalogIdQueryType }) =>
    async (dispatch, getState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                payload: catalogId,
                type: LOAD_CATALOG_ITEM_MODELS_REQUEST,
            });

            const { payload } = await itemCardModelApi.fetchItemsCardModelsByCatalogId({
                authToken,
                catalogId,
                deployment,
                query,
            });

            return dispatch({
                meta: {
                    actionTime: Date.now(),
                    catalogId,
                    pageCount: payload.pageCount,
                    query,
                    recordCount: payload.recordCount,
                    totalRecords: payload.totalRecords,
                },
                payload: payload.items,
                type: LOAD_CATALOG_ITEM_MODELS_SUCCESS,
            } as LOAD_CATALOG_ITEM_MODELS_SUCCESS_ACTION);
        } catch (error) {
            dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: LOAD_CATALOG_ITEM_MODELS_FAIL,
            });
        }
    };
